import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import EditableCell from '../common/EditableCell';
import ConfirmDialog from '../common/ConfirmDialog';
import TrackingToolbar from '../common/TrackingToolbar';
import FilterDropdown from '../common/FilterDropdown';
import { RowDeleteButton } from '../common/DeleteButton';
import ExportOptionsModal from '../common/ExportOptionsModal';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import useTableFilter from '../../hooks/useTableFilter';
import useTableSort from '../../hooks/useTableSort';
import useColumnVisibility from '../../hooks/useColumnVisibility';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import useTableSelection from '../../hooks/useTableSelection';
import useFreezePane from '../../hooks/useFreezePane';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import ChangeOrdersAnalyticsDashboard from './ChangeOrdersAnalyticsDashboard';

// Dashboard columns (1-29) - shown by default
const DASHBOARD_COLUMNS = [
  // Identification columns (dark blue)
  { key: 'change_request_number_lgl', label: 'CR# LGL', type: 'text', width: 100, category: 'identification' },
  { key: 'change_type_fwo_ccr', label: 'Type (FWO/CCR)', type: 'text', width: 110, category: 'identification' },
  { key: 'ccr_or_fwo_id', label: 'CCR/FWO ID', type: 'text', width: 100, category: 'identification' },
  { key: 'extra_number', label: 'Extra #', type: 'text', width: 80, category: 'identification' },
  { key: 'change_order_number_client', label: 'Client CO#', type: 'text', width: 100, category: 'identification' },
  { key: 'subject', label: 'Subject', type: 'text', width: 200, category: 'identification' },
  { key: 'area', label: 'Area', type: 'text', width: 100, category: 'identification' },
  { key: 'sub_area', label: 'Sub Area', type: 'text', width: 100, category: 'identification' },
  { key: 'discipline', label: 'Discipline', type: 'text', width: 100, category: 'identification' },
  { key: 'contract_type_costplus_timeandmaterial_lumpsum_unitprice', label: 'Contract Type', type: 'text', width: 120, category: 'identification' },
  // Impact columns (cyan)
  { key: 'impact_schedule', label: 'Impact Schedule', type: 'text', width: 120, category: 'impact' },
  { key: 'impact_cost', label: 'Impact Cost CAD', type: 'number', width: 120, category: 'impact' },
  // Budget columns (gray)
  { key: 'budget_hour_estimated', label: 'Budget Hours', type: 'number', width: 110, category: 'budget' },
  { key: 'budget_cost_labour', label: 'Budget Labour CAD', type: 'number', width: 130, category: 'budget' },
  { key: 'budget_cost_non_labour', label: 'Budget Non-Labour CAD', type: 'number', width: 150, category: 'budget' },
  { key: 'budget_cost_total', label: 'Budget Total CAD', type: 'number', width: 130, category: 'budget' },
  // Date columns (purple)
  { key: 'date_issued_ccr', label: 'Date Issued', type: 'date', width: 110, category: 'date' },
  { key: 'date_signed_by_client_ccr', label: 'Date Signed', type: 'date', width: 110, category: 'date' },
  { key: 'date_cco', label: 'Date CCO', type: 'date', width: 100, category: 'date' },
  { key: 'treatment_time', label: 'Treatment Time', type: 'text', width: 110, category: 'date' },
  // Tracking columns (green)
  { key: 'tracking_method', label: 'Tracking Method', type: 'text', width: 120, category: 'tracking' },
  { key: 'progress_percentage', label: 'Progress %', type: 'number', width: 90, isProgress: true, category: 'tracking' },
  // Actuals columns (red)
  { key: 'actual_spent_hour', label: 'Actual Hours', type: 'number', width: 100, category: 'actuals' },
  { key: 'earned_hrs', label: 'Earned Hours', type: 'number', width: 100, category: 'actuals' },
  // Performance columns (lime green)
  { key: 'variance_earned_to_spent', label: 'Variance', type: 'number', width: 90, category: 'performance' },
  { key: 'cpi_per_ccr', label: 'CPI', type: 'number', width: 70, category: 'performance' },
  // Estimated & Submitted columns (orange)
  { key: 'estimated_price', label: 'Estimated $', type: 'number', width: 110, category: 'estimated' },
  { key: 'submitted_hours', label: 'Submitted Hours', type: 'number', width: 120, category: 'estimated' },
  // Maestro column (teal)
  { key: 'maestro_status', label: 'Maestro', type: 'select', width: 100, category: 'maestro', options: ['', 'open', 'approved'] },
  // Reference columns (dark)
  { key: 'reference_document_or_hyper_links', label: 'Reference/Links', type: 'text', width: 150, category: 'reference' },
  { key: 'comments', label: 'Comments', type: 'text', width: 200, category: 'reference' },
  { key: 'responsible', label: 'Responsible', type: 'text', width: 120, category: 'reference' },
];

// UDF columns (30-45) - configurable, hidden by default
const UDF_COLUMNS = [
  { key: 'udf01', label: 'UDF01', type: 'text', width: 100 },
  { key: 'udf02', label: 'UDF02', type: 'text', width: 100 },
  { key: 'udf03', label: 'UDF03', type: 'text', width: 100 },
  { key: 'udf04', label: 'UDF04', type: 'text', width: 100 },
  { key: 'udf05', label: 'UDF05', type: 'text', width: 100 },
  { key: 'udf06', label: 'UDF06', type: 'text', width: 100 },
  { key: 'udf07', label: 'UDF07', type: 'text', width: 100 },
  { key: 'udf08', label: 'UDF08', type: 'text', width: 100 },
  { key: 'udf09', label: 'UDF09', type: 'text', width: 100 },
  { key: 'udf10', label: 'UDF10', type: 'text', width: 100 },
  { key: 'udf11', label: 'UDF11', type: 'text', width: 100 },
  { key: 'udf12', label: 'UDF12', type: 'text', width: 100 },
  { key: 'udf13', label: 'UDF13', type: 'text', width: 100 },
  { key: 'udf14', label: 'UDF14', type: 'text', width: 100 },
  { key: 'udf15', label: 'UDF15', type: 'text', width: 100 },
  { key: 'udf16', label: 'UDF16', type: 'text', width: 100 },
];

// All columns combined
const ALL_COLUMNS = [...DASHBOARD_COLUMNS, ...UDF_COLUMNS];

// Format value for display
const formatTrackingValue = (val, column) => {
  if (val === null || val === undefined || val === '') return '-';
  if (column.type === 'number') {
    if (column.isProgress) return `${Number(val).toFixed(1)}%`;
    return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (column.type === 'date' && val) {
    try {
      const date = new Date(val);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return val;
    }
  }
  if (column.key === 'maestro_status' && val) {
    return val.charAt(0).toUpperCase() + val.slice(1);
  }
  return val;
};

// Get cell background color based on category
const getCellCategoryColor = (val, column) => {
  // Progress percentage coloring
  if (column.isProgress && val != null) {
    const n = Number(val);
    if (n >= 100) return '#d4edda'; // Green - complete
    if (n >= 50) return '#fff3cd';  // Yellow - in progress
    if (n > 0) return '#f8d7da';    // Red - started
  }
  // Maestro status coloring
  if (column.key === 'maestro_status' && val) {
    if (val === 'approved') return '#d4edda';
    if (val === 'open') return '#fff3cd';
  }
  return 'transparent';
};

// Get header background color based on category
const getHeaderColor = (column) => {
  const colors = {
    identification: '#343a40', // Dark blue/gray
    impact: '#17a2b8',         // Cyan
    budget: '#6c757d',         // Gray
    date: '#9c27b0',           // Purple
    tracking: '#28a745',       // Green
    actuals: '#dc3545',        // Red
    performance: '#8bc34a',    // Lime green
    estimated: '#e65100',      // Orange
    maestro: '#00695c',        // Teal
    reference: '#495057',      // Dark gray
  };
  return colors[column.category] || '#343a40'; // Default dark
};

const ChangeOrdersTracking = ({ 
  projectId, 
  jobNumber,
  onUploadClick,
  onExportClick,
  onDownloadTemplateClick,
  onColumnConfigClick,
}) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast, showToast } = useToast();
  const { confirm, dialogProps } = useConfirm();
  const [savingRows, setSavingRows] = useState(new Set());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Live data push: ref + callback for real-time refresh
  const fetchItemsRef = useRef(null);

  const handleDataChange = useCallback(() => {
    if (fetchItemsRef.current) fetchItemsRef.current();
  }, []);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState, socketId } = usePresence('changeOrders', projectId || jobNumber || 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  // Use reusable hooks for filtering, sorting, column visibility, and selection
  const filterHook = useTableFilter({ columns: ALL_COLUMNS, items });
  const sortHook = useTableSort({ columns: ALL_COLUMNS });
  const columnHook = useColumnVisibility({
    allColumns: ALL_COLUMNS,
    defaultColumns: DASHBOARD_COLUMNS,
    storageKey: 'ccr_visible_columns',
  });

  // Get currently visible columns
  const columns = columnHook.displayedColumns;

  // Freeze pane hook - freeze checkbox (40px) and actions (60px) columns
  const freezePane = useFreezePane({
    frozenColumnCount: 2,
    frozenColumnWidths: [40, 60],
    headerBgColor: '#795548',
    frozenBgColor: '#ffffff',
  });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = jobNumber 
        ? `/ccr-tracking?job_number=${encodeURIComponent(jobNumber)}` 
        : '/ccr-tracking';
      const response = await authenticatedApiCall(instance, accounts, 'GET', endpoint);
      if (response.data.success) {
        setItems(response.data.data || []);
      } else {
        setError('Failed to load data');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => { fetchItemsRef.current = fetchItems; }, [fetchItems]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Apply search, filters, and sorting
  const filteredAndSortedItems = useMemo(() => {
    let result = [...items];

    // Apply global search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(item =>
        ALL_COLUMNS.some(col => {
          const val = item[col.key];
          return val !== null && val !== undefined && String(val).toLowerCase().includes(search);
        })
      );
    }

    // Apply column filters
    result = filterHook.applyFilters(result);

    // Apply sorting
    result = sortHook.applySorting(result);

    return result;
  }, [items, searchTerm, filterHook.selectedValues, filterHook.textFilters, filterHook.numericFilters, filterHook.dateFilters, filterHook.applyFilters, sortHook.sortColumn, sortHook.sortDirection, sortHook.applySorting]);

  // Selection hook - must be after filteredAndSortedItems
  const selectionHook = useTableSelection({ 
    items: filteredAndSortedItems, 
    getItemId: (item) => item.id 
  });

  // Combined active filters check
  const hasActiveFilters = filterHook.hasActiveFilters || searchTerm;

  // Clear all filters including search and sort
  const handleClearAll = useCallback(() => {
    filterHook.clearAllFilters();
    setSearchTerm('');
    sortHook.clearSort();
    showToast('All filters cleared', 'success');
  }, [filterHook, sortHook, showToast]);

  const handleSaveCell = async (rowId, columnKey, newValue) => {
    setSavingRows(prev => new Set(prev).add(rowId));
    try {
      const col = ALL_COLUMNS.find(c => c.key === columnKey);
      let processedValue = newValue;
      
      if (col?.type === 'number') {
        processedValue = newValue === '' ? null : Number(newValue);
      } else if (col?.type === 'date') {
        processedValue = newValue === '' ? null : newValue;
      }
      
      const response = await authenticatedApiCall(
        instance, 
        accounts, 
        'PUT', 
        `/ccr-tracking/${rowId}`, 
        { [columnKey]: processedValue },
        syncHeaders()
      );
      
      if (response.data.success) {
        setItems(prev => prev.map(item => 
          item.id === rowId ? { ...item, [columnKey]: processedValue } : item
        ));
        showToast('Saved successfully');
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      showToast(`Save failed: ${err.message}`, 'error');
      throw err;
    } finally {
      setSavingRows(prev => {
        const s = new Set(prev);
        s.delete(rowId);
        return s;
      });
    }
  };

  const handleAddNewRow = async () => {
    try {
      const rowData = { ...newRow };
      if (jobNumber) rowData.job_number = jobNumber;
      
      const response = await authenticatedApiCall(
        instance, 
        accounts, 
        'POST', 
        '/ccr-tracking', 
        rowData,
        syncHeaders()
      );
      
      if (response.data.success) {
        setItems(prev => [response.data.data, ...prev]);
        setNewRow({});
        setIsAddingNew(false);
        showToast('New change order added');
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      showToast(`Failed: ${err.message}`, 'error');
    }
  };

  const handleDeleteRow = async (rowId) => {
    const confirmed = await confirm({
      title: 'Delete Change Order',
      message: 'Are you sure you want to delete this change order record?',
      confirmText: 'Delete',
      type: 'danger',
    });
    
    if (!confirmed) return;
    
    try {
      const response = await authenticatedApiCall(
        instance, 
        accounts, 
        'DELETE', 
        `/ccr-tracking/${rowId}`,
        null,
        syncHeaders()
      );
      
      if (response.data.success) {
        setItems(prev => prev.filter(item => item.id !== rowId));
        selectionHook.removeFromSelection([rowId]);
        showToast('Deleted');
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  };

  // Batch delete handler
  const handleBatchDelete = async () => {
    const count = selectionHook.selectedCount;
    const confirmed = await confirm({
      title: 'Delete Multiple Change Orders',
      message: `Are you sure you want to delete ${count} selected change order(s)?`,
      confirmText: `Delete ${count}`,
      type: 'danger',
    });
    
    if (!confirmed) return;
    
    try {
      const idsToDelete = selectionHook.selectedIds;
      let successCount = 0;
      let failCount = 0;
      
      for (const id of idsToDelete) {
        try {
          const response = await authenticatedApiCall(
            instance, 
            accounts, 
            'DELETE', 
            `/ccr-tracking/${id}`,
            null,
            syncHeaders()
          );
          if (response.data.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch {
          failCount++;
        }
      }
      
      // Refresh the items after deletion
      if (successCount > 0) {
        setItems(prev => prev.filter(item => !idsToDelete.includes(item.id)));
        selectionHook.clearSelection();
        showToast(`Deleted ${successCount} record(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
      } else {
        showToast('Delete failed', 'error');
      }
    } catch (err) {
      showToast(`Batch delete failed: ${err.message}`, 'error');
    }
  };

  // Export handler with options
  const handleExport = async (exportType) => {
    setIsExporting(true);
    try {
      // Call the parent export handler with the export type
      if (onExportClick) {
        await onExportClick(exportType);
      }
      setShowExportModal(false);
      showToast('Export completed');
    } catch (err) {
      showToast(`Export failed: ${err.message}`, 'error');
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate table min width based on visible columns (+ checkbox column + actions column)
  const tableMinWidth = columns.reduce((sum, col) => sum + col.width, 0) + 40 + 60;
  
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ 
          display: 'inline-block', 
          width: '48px', 
          height: '48px', 
          border: '4px solid #f3f3f3', 
          borderTop: '4px solid #795548', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
        <p style={{ marginTop: '1rem', color: '#666' }}>Loading change orders data...</p>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '2rem', 
        backgroundColor: '#fff3cd', 
        border: '1px solid #ffc107', 
        borderRadius: '8px', 
        color: '#856404' 
      }}>
        <strong>Error:</strong> {error}
        <button 
          onClick={fetchItems} 
          style={{ 
            marginLeft: '1rem', 
            padding: '0.5rem 1rem', 
            backgroundColor: '#795548', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer' 
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {toast.show && (
        <div style={{ 
          position: 'fixed', 
          top: '20px', 
          right: '20px', 
          padding: '1rem 1.5rem', 
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545', 
          color: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
          zIndex: 9999 
        }}>
          {toast.message}
        </div>
      )}
      
      <ConfirmDialog {...dialogProps} />

      {/* Export Options Modal */}
      <ExportOptionsModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
        title="Export Change Orders"
        accentColor="#795548"
      />


      {/* Toolbar using reusable component */}
      <TrackingToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search change orders..."
        filteredCount={filteredAndSortedItems.length}
        totalCount={items.length}
        activeFilters={filterHook.activeFilters}
        hasActiveFilters={hasActiveFilters}
        onClearFilter={filterHook.clearColumn}
        onClearAll={handleClearAll}
        onImport={onUploadClick}
        onExport={() => setShowExportModal(true)}
        onDownloadTemplate={onDownloadTemplateClick}
        onColumnConfig={() => setShowColumnConfig(true)}
        onAnalytics={() => setShowAnalytics(true)}
        onAddNew={() => setIsAddingNew(true)}
        selectedCount={selectionHook.selectedCount}
        onBatchDelete={handleBatchDelete}
        accentColor="#795548"
        customContent={
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
        }
      />

      {/* Add New Row Form */}
      {isAddingNew && (
        <div style={{ 
          backgroundColor: '#efebe9', 
          padding: '1rem', 
          borderRadius: '8px', 
          marginBottom: '1rem', 
          border: '1px solid #795548' 
        }}>
          <h4 style={{ margin: '0 0 1rem 0', color: '#5d4037' }}>Add New Change Order</h4>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', 
            gap: '0.75rem' 
          }}>
            {DASHBOARD_COLUMNS.slice(0, 16).map(col => (
              <div key={col.key}>
                <label style={{ 
                  fontSize: '0.8rem', 
                  color: '#666', 
                  display: 'block', 
                  marginBottom: '4px' 
                }}>
                  {col.label}
                </label>
                {col.type === 'select' && col.options ? (
                  <select
                    value={newRow[col.key] || ''}
                    onChange={(e) => setNewRow(prev => ({ ...prev, [col.key]: e.target.value }))}
                    style={{ 
                      width: '100%', 
                      padding: '6px', 
                      borderRadius: '4px', 
                      border: '1px solid #ccc', 
                      boxSizing: 'border-box' 
                    }}
                  >
                    <option value="">--</option>
                    {col.options.filter(o => o !== '').map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input 
                    type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'} 
                    step="0.01" 
                    value={newRow[col.key] || ''} 
                    onChange={(e) => setNewRow(prev => ({ ...prev, [col.key]: e.target.value }))} 
                    style={{ 
                      width: '100%', 
                      padding: '6px', 
                      borderRadius: '4px', 
                      border: '1px solid #ccc', 
                      boxSizing: 'border-box' 
                    }} 
                  />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            <button 
              onClick={handleAddNewRow} 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer' 
              }}
            >
              ✓ Save
            </button>
            <button 
              onClick={() => { setIsAddingNew(false); setNewRow({}); }} 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#6c757d', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer' 
              }}
            >
              ✕ Cancel
            </button>
          </div>
        </div>
      )}

      {/* Column Config Modal */}
      {showColumnConfig && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Column Configuration</h3>
            <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
              Show, hide, and rearrange columns. Drag ⠿ or use ↑ ↓. Your layout is saved on this browser.
            </p>
            <ColumnConfigPanel
              columns={columnHook.orderedColumns}
              isVisible={columnHook.isColumnVisible}
              onToggle={columnHook.toggleColumn}
              onMove={columnHook.moveColumn}
              onReset={() => { columnHook.resetToDefault(); columnHook.resetOrder(); }}
              compact={false}
            />
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={() => columnHook.resetToDefault()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Reset to Default
              </button>
              <button
                onClick={() => setShowColumnConfig(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#795548',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div style={{ 
        flex: 1, 
        ...freezePane.containerStyle,
        border: '1px solid #dee2e6', 
        borderRadius: '8px', 
        backgroundColor: 'white' 
      }}>
        <table style={{ 
          width: '100%', 
          ...freezePane.tableStyle,
          fontSize: '0.85rem', 
          minWidth: `${tableMinWidth}px` 
        }}>
          <thead style={freezePane.theadStyle}>
            <tr>
              {/* Checkbox column - frozen */}
              <th style={freezePane.getHeaderCellStyle(0, {
                padding: '8px',
                textAlign: 'center',
                width: '40px',
                minWidth: '40px',
                borderRight: '1px solid rgba(255,255,255,0.2)',
                borderBottom: '1px solid #dee2e6',
                color: 'white',
                verticalAlign: 'middle',
              })}>
                <input
                  type="checkbox"
                  checked={selectionHook.isAllSelected(filteredAndSortedItems)}
                  ref={(el) => {
                    if (el) el.indeterminate = selectionHook.isSomeSelected(filteredAndSortedItems);
                  }}
                  onChange={() => selectionHook.toggleSelectAll(filteredAndSortedItems)}
                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  title={selectionHook.isAllSelected(filteredAndSortedItems) ? 'Deselect all' : 'Select all'}
                />
              </th>
              {/* Actions column - frozen */}
              <th style={freezePane.getHeaderCellStyle(1, {
                padding: '8px',
                textAlign: 'center',
                width: '60px',
                minWidth: '60px',
                borderRight: '1px solid rgba(255,255,255,0.2)',
                borderBottom: '1px solid #dee2e6',
                color: 'white',
                verticalAlign: 'top',
              })}>
                Actions
              </th>
              {columns.map((col, colIndex) => {
                const hasFilter = filterHook.hasColumnFilter(col.key);
                return (
                  <th
                    key={col.key}
                    style={freezePane.getHeaderCellStyle(colIndex + 2, {
                      padding: '6px 8px',
                      width: col.width,
                      minWidth: col.width,
                      borderRight: '1px solid rgba(255,255,255,0.2)',
                      borderBottom: '1px solid #dee2e6',
                      backgroundColor: getHeaderColor(col),
                      color: 'white',
                      verticalAlign: 'top',
                    })}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '40px' }}>
                      {/* Top: Label and sort */}
                      <div 
                        onClick={() => sortHook.handleSort(col.key)}
                        style={{ 
                          flex: 1, 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start',
                          gap: '4px',
                          cursor: 'pointer',
                          userSelect: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span>{col.label}</span>
                        {sortHook.sortColumn === col.key && (
                          <span style={{ fontSize: '0.7rem' }}>
                            {sortHook.sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                      {/* Bottom: Filter icon */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            filterHook.setFilterSearch('');
                            filterHook.setOpenFilterColumn(
                              filterHook.openFilterColumn === col.key ? null : col.key
                            );
                          }}
                          style={{
                            cursor: 'pointer',
                            opacity: hasFilter ? 1 : 0.5,
                            fontSize: '0.75rem',
                          }}
                          title="Filter"
                        >
                          🔽
                        </span>
                      </div>
                    </div>
                    
                    {/* Filter Dropdown */}
                    {filterHook.openFilterColumn === col.key && (
                      <FilterDropdown
                        column={col}
                        uniqueValues={filterHook.getUniqueValues(col.key)}
                        selectedValues={filterHook.selectedValues[col.key] || []}
                        textFilter={filterHook.textFilters[col.key] || null}
                        numericFilter={filterHook.numericFilters[col.key] || null}
                        dateFilter={filterHook.dateFilters[col.key] || null}
                        filterSearch={filterHook.filterSearch}
                        onToggleValue={filterHook.toggleValue}
                        onSelectAll={filterHook.selectAll}
                        onClearColumn={filterHook.clearColumn}
                        onApplyTextFilter={filterHook.applyTextFilter}
                        onApplyNumericFilter={filterHook.applyNumericFilter}
                        onApplyDateFilter={filterHook.applyDateFilter}
                        onClose={() => {
                          filterHook.setOpenFilterColumn(null);
                          filterHook.setFilterSearch('');
                        }}
                        onSearchChange={filterHook.setFilterSearch}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedItems.length === 0 ? (
              <tr>
                <td 
                  colSpan={columns.length + 2} 
                  style={{ 
                    padding: '3rem', 
                    textAlign: 'center', 
                    color: '#6c757d' 
                  }}
                >
                  {searchTerm ? 'No matching records' : 'No change orders. Click "Add New" to create one.'}
                </td>
              </tr>
            ) : (
              filteredAndSortedItems.map((item, index) => (
                <tr 
                  key={item.id} 
                  style={{ 
                    backgroundColor: selectionHook.isSelected(item.id) 
                      ? '#e3f2fd' 
                      : (index % 2 === 0 ? 'white' : '#f8f9fa'), 
                    borderBottom: '1px solid #dee2e6' 
                  }}
                >
                  {/* Checkbox cell - frozen */}
                  <td style={freezePane.getBodyCellStyle(0, index, {
                    padding: '4px 8px',
                    textAlign: 'center',
                    width: '40px',
                    minWidth: '40px',
                    borderRight: '1px solid #eee',
                    borderBottom: '1px solid #dee2e6',
                  }, selectionHook.isSelected(item.id))}>
                    <input
                      type="checkbox"
                      checked={selectionHook.isSelected(item.id)}
                      onChange={() => selectionHook.toggleRow(item.id)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </td>
                  {/* Actions cell - frozen */}
                  <td style={freezePane.getBodyCellStyle(1, index, {
                    padding: '4px 8px',
                    textAlign: 'center',
                    width: '60px',
                    minWidth: '60px',
                    borderRight: '1px solid #eee',
                    borderBottom: '1px solid #dee2e6',
                  }, selectionHook.isSelected(item.id))}>
                    <RowDeleteButton
                      onClick={() => handleDeleteRow(item.id)}
                      title="Delete this change order"
                    />
                  </td>
                  {columns.map(col => (
                    <td 
                      key={col.key} 
                      style={{ 
                        padding: '4px 8px', 
                        textAlign: col.type === 'number' ? 'right' : 'left', 
                        borderRight: '1px solid #eee', 
                        backgroundColor: savingRows.has(item.id) ? '#fff3cd' : getCellCategoryColor(item[col.key], col)
                      }}
                    >
                      <EditableCell 
                        value={item[col.key]} 
                        row={item} 
                        column={col} 
                        onSave={handleSaveCell} 
                        disabled={false} 
                        formatValue={formatTrackingValue}
                        cellBackgroundColor={getCellCategoryColor}
                        accentColor="#795548"
                      />
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <ChangeOrdersAnalyticsDashboard
          data={items}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  );
};

export default ChangeOrdersTracking;
